import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Hangar, Obstacle, ObstacleType } from "../../../types";
import { formatNumber, uuidv4 } from "../../../utils";
import { feetToMeters } from "../../../utils/math";
import { OBSTACLE_STYLES } from "../../../widgets/Layout/widgets/Obstacle";
import {
  extractPointsFromGeometry,
  findClosestEdge,
  getPolygonBounds,
  getPolygonGeometry,
  isPointInPolygon,
  Point,
  translatePolygon,
} from "./obstacleUtils";

type Props = {
  hangar: Hangar;
  feetToPixels: number;
  obstacle: Obstacle;
  onDrag: (obstacle: Obstacle) => void;
  onDelete: (obstacle: Obstacle) => void;
  onCopy?: (obstacle: Obstacle) => void;
  isMetric?: boolean;
};

export const DraggablePolygonObstacle: React.FC<Props> = ({
  hangar,
  feetToPixels,
  obstacle,
  onDrag,
  onDelete,
  onCopy,
  isMetric = false,
}) => {
  const { t } = useTranslation();
  const svgRef = React.useRef<SVGSVGElement>(null);

  // State for dragging
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragStartPos, setDragStartPos] = React.useState<Point | null>(null);
  const [draggingPointIndex, setDraggingPointIndex] = React.useState<number | null>(null);

  // Context menu
  const [contextMenu, setContextMenu] = React.useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);

  // Extract points from geometry
  const points = React.useMemo(
    () => extractPointsFromGeometry(obstacle.geom),
    [obstacle.geom]
  );

  // Calculate edge info for labels
  const edgeInfo = React.useMemo(() => {
    return points.map((point, i) => {
      const nextPoint = points[(i + 1) % points.length];
      const midX = ((point.x + nextPoint.x) / 2) * feetToPixels;
      const midY = ((point.y + nextPoint.y) / 2) * feetToPixels;
      const lengthFeet = Math.hypot(nextPoint.x - point.x, nextPoint.y - point.y);
      const angle = Math.atan2(nextPoint.y - point.y, nextPoint.x - point.x) * (180 / Math.PI);
      // Keep text readable (not upside down)
      const displayAngle = angle > 90 || angle < -90 ? angle + 180 : angle;
      return { midX, midY, lengthFeet, displayAngle };
    });
  }, [points, feetToPixels]);

  // Get styling based on obstacle type
  const style = OBSTACLE_STYLES[obstacle.type || ObstacleType.OBSTRUCTION];
  const fillColor = style?.backgroundColor || "transparent";
  const strokeColor = style?.outlineColor || "#666";
  const strokeDash = obstacle.type === ObstacleType.NO_TOW_AREA ? "5,5" : undefined;

  // Convert points to SVG polygon string
  const polygonPoints = points
    .map((p) => `${p.x * feetToPixels},${p.y * feetToPixels}`)
    .join(" ");

  // Handle mouse down on polygon (start drag)
  const handlePolygonMouseDown = (event: React.MouseEvent) => {
    if (event.button !== 0) return; // Left click only

    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;

    const mouseX = (event.clientX - rect.left) / feetToPixels;
    const mouseY = (event.clientY - rect.top) / feetToPixels;

    // Check for modifier keys
    const isMetaKey = event.metaKey || event.ctrlKey;
    const isAltKey = event.altKey;

    if (isMetaKey) {
      // Cmd+click: Insert point on closest edge
      const closest = findClosestEdge({ x: mouseX, y: mouseY }, points);
      if (closest && closest.distance < 10 / feetToPixels) {
        const newPoints = [...points];
        newPoints.splice(closest.edgeIndex + 1, 0, closest.closestPoint);
        updateObstaclePoints(newPoints);
      }
      event.stopPropagation();
      return;
    }

    // Start whole-polygon drag
    if (isPointInPolygon({ x: mouseX, y: mouseY }, points)) {
      setIsDragging(true);
      setDragStartPos({ x: mouseX, y: mouseY });
      event.stopPropagation();
    }
  };

  // Handle mouse down on point (start point drag)
  const handlePointMouseDown = (event: React.MouseEvent, index: number) => {
    event.stopPropagation();
    if (event.button !== 0) return;

    const isAltKey = event.altKey;

    if (isAltKey && points.length > 3) {
      // Alt+click: Delete point (minimum 3 points)
      const newPoints = points.filter((_, i) => i !== index);
      updateObstaclePoints(newPoints);
      return;
    }

    // Start point drag
    setDraggingPointIndex(index);
    setIsDragging(true);
  };

  // Handle mouse move
  const handleMouseMove = (event: React.MouseEvent) => {
    if (!isDragging) return;

    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;

    const mouseX = (event.clientX - rect.left) / feetToPixels;
    const mouseY = (event.clientY - rect.top) / feetToPixels;

    if (draggingPointIndex !== null) {
      // Dragging a single point
      const newPoints = [...points];
      newPoints[draggingPointIndex] = {
        x: Math.max(0, Math.min(hangar.width, mouseX)),
        y: Math.max(0, Math.min(hangar.depth, mouseY)),
      };
      updateObstaclePoints(newPoints);
    } else if (dragStartPos) {
      // Dragging whole polygon
      const dx = mouseX - dragStartPos.x;
      const dy = mouseY - dragStartPos.y;

      const bounds = getPolygonBounds(points);
      const newX = Math.max(0, Math.min(hangar.width - bounds.width, bounds.x + dx));
      const newY = Math.max(0, Math.min(hangar.depth - bounds.depth, bounds.y + dy));
      const clampedDx = newX - bounds.x;
      const clampedDy = newY - bounds.y;

      if (clampedDx !== 0 || clampedDy !== 0) {
        const newPoints = translatePolygon(points, clampedDx, clampedDy);
        updateObstaclePoints(newPoints);
        setDragStartPos({ x: dragStartPos.x + clampedDx, y: dragStartPos.y + clampedDy });
      }
    }
  };

  // Handle mouse up
  const handleMouseUp = () => {
    setIsDragging(false);
    setDragStartPos(null);
    setDraggingPointIndex(null);
  };

  // Update obstacle with new points
  const updateObstaclePoints = (newPoints: Point[]) => {
    const geom = getPolygonGeometry(newPoints);
    const bounds = getPolygonBounds(newPoints);

    onDrag({
      ...obstacle,
      geom,
      x: bounds.x,
      y: bounds.y,
      width: bounds.width,
      depth: bounds.depth,
    });
  };

  // Context menu handlers
  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 }
        : null
    );
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleCopy = () => {
    if (!onCopy) return;

    // Create a copy offset by (5, 5) feet
    const newPoints = translatePolygon(points, 5, 5);
    const geom = getPolygonGeometry(newPoints);
    const bounds = getPolygonBounds(newPoints);

    const copiedObstacle: Obstacle = {
      ...obstacle,
      id: uuidv4(),
      geom,
      x: bounds.x,
      y: bounds.y,
      width: bounds.width,
      depth: bounds.depth,
    };

    onCopy(copiedObstacle);
    handleCloseContextMenu();
  };

  const handleDelete = () => {
    onDelete(obstacle);
    handleCloseContextMenu();
  };

  // Add window-level mouse handlers for drag
  React.useEffect(() => {
    if (isDragging) {
      const handleWindowMouseMove = (event: MouseEvent) => {
        const rect = svgRef.current?.getBoundingClientRect();
        if (!rect) return;

        const mouseX = (event.clientX - rect.left) / feetToPixels;
        const mouseY = (event.clientY - rect.top) / feetToPixels;

        if (draggingPointIndex !== null) {
          const newPoints = [...points];
          newPoints[draggingPointIndex] = {
            x: Math.max(0, Math.min(hangar.width, mouseX)),
            y: Math.max(0, Math.min(hangar.depth, mouseY)),
          };
          updateObstaclePoints(newPoints);
        } else if (dragStartPos) {
          const dx = mouseX - dragStartPos.x;
          const dy = mouseY - dragStartPos.y;

          const bounds = getPolygonBounds(points);
          const newX = Math.max(0, Math.min(hangar.width - bounds.width, bounds.x + dx));
          const newY = Math.max(0, Math.min(hangar.depth - bounds.depth, bounds.y + dy));
          const clampedDx = newX - bounds.x;
          const clampedDy = newY - bounds.y;

          if (clampedDx !== 0 || clampedDy !== 0) {
            const newPoints = translatePolygon(points, clampedDx, clampedDy);
            updateObstaclePoints(newPoints);
            setDragStartPos({ x: dragStartPos.x + clampedDx, y: dragStartPos.y + clampedDy });
          }
        }
      };

      const handleWindowMouseUp = () => {
        setIsDragging(false);
        setDragStartPos(null);
        setDraggingPointIndex(null);
      };

      window.addEventListener("mousemove", handleWindowMouseMove);
      window.addEventListener("mouseup", handleWindowMouseUp);

      return () => {
        window.removeEventListener("mousemove", handleWindowMouseMove);
        window.removeEventListener("mouseup", handleWindowMouseUp);
      };
    }
  }, [isDragging, draggingPointIndex, dragStartPos, points, hangar, feetToPixels]);

  const bounds = getPolygonBounds(points);

  return (
    <>
      <svg
        ref={svgRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: hangar.width * feetToPixels,
          height: hangar.depth * feetToPixels,
          pointerEvents: "none",
          zIndex: obstacle.type === ObstacleType.OBSTRUCTION ? 2 : 1,
        }}
        onContextMenu={handleContextMenu}
      >
        {/* Polygon shape */}
        <polygon
          points={polygonPoints}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={2}
          strokeDasharray={strokeDash}
          style={{
            pointerEvents: "all",
            cursor: isDragging ? "grabbing" : "grab",
          }}
          onMouseDown={handlePolygonMouseDown}
        />

        {/* Point handles */}
        {points.map((point, index) => (
          <circle
            key={index}
            cx={point.x * feetToPixels}
            cy={point.y * feetToPixels}
            r={6}
            fill={draggingPointIndex === index ? "#1976d2" : "#fff"}
            stroke="#1976d2"
            strokeWidth={2}
            style={{
              pointerEvents: "all",
              cursor: "move",
            }}
            onMouseDown={(e) => handlePointMouseDown(e, index)}
          />
        ))}

        {/* Edge length labels */}
        {edgeInfo.map((edge, index) => {
          const label = isMetric
            ? `${formatNumber(feetToMeters(edge.lengthFeet), 1)} m`
            : `${formatNumber(edge.lengthFeet, 1)} ft`;
          return (
            <text
              key={`edge-${index}`}
              x={edge.midX}
              y={edge.midY}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="#333"
              fontSize={10}
              fontFamily="Lexend Deca, sans-serif"
              transform={`rotate(${edge.displayAngle}, ${edge.midX}, ${edge.midY})`}
              style={{ pointerEvents: "none" }}
            >
              {label}
            </text>
          );
        })}
      </svg>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleCopy}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {t("settings.hangarSettings.obstacleContextMenu.copy", "Copy")}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {t("settings.hangarSettings.obstacleContextMenu.delete", "Delete")}
          </ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
